import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { CreateEditTeamMeetingForm, ICreateEditTeamMeetingFormState } from ".";
import { useDeleteMeeting, useEditTeamMeeting, useGetTeamMeetingDetail } from "../../../../http";
import { IUser } from "../../../../models";
import { useDestructiveConfirm } from "../../../../utilities";
import { useTeamPageLayoutContext } from "../../TeamPageLayout";

interface IProps {
  visible: boolean;
  meetingGuid: string;
  onSaved: () => void;
  onCancelled: () => void;
}

const EditTeamMeetingModal = (props: IProps) => {
  const { team } = useTeamPageLayoutContext();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading } = useGetTeamMeetingDetail(props.meetingGuid, props.visible);
  const { mutate: editMeeting, isLoading: isSaving } = useEditTeamMeeting();
  const { mutate: deleteMeeting, isLoading: isDeleting } = useDeleteMeeting();
  const confirm = useDestructiveConfirm();

  const { formState, control, handleSubmit, reset } = useForm<ICreateEditTeamMeetingFormState>({
    defaultValues: {
      title: "",
      description: "",
      externalCallUrl: "",
      invitees: [] as IUser[],
      agendaItems: [],
    },
  });

  useEffect(() => {
    if (!isLoading && data) {
      reset({
        title: data.title,
        description: data.description,
        externalCallUrl: data.externalCallUrl ?? "",
        invitees: data.invitees,
        agendaItems: data.agendaItems,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, reset]);

  async function handleSaveClicked() {
    handleSubmit((data) => {
      editMeeting(
        {
          teamGuid: team.guid,
          meetingGuid: props.meetingGuid!,
          title: data.title,
          description: data.description,
          externalCallUrl: data.externalCallUrl,
          invitees: data.invitees,
          agendaSteps: data.agendaItems,
        },
        {
          onSuccess: () => {
            reset(undefined, { keepDefaultValues: true });
            props.onSaved();
          },
        }
      );
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  async function handleDeleteClicked() {
    const { confirmed } = await confirm({
      title: "Delete Meeting",
      content: "Are you sure you want to delete this meeting?",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteMeeting(
      { meetingGuid: props.meetingGuid, teamGuid: team.guid },
      {
        onSuccess: () => {
          reset(undefined, { keepDefaultValues: true });
          props.onCancelled();
          enqueueSnackbar(`Meeting has been deleted`);
        },
      }
    );

    reset(undefined, { keepDefaultValues: true });
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle sx={{ mb: 0 }}>Edit Team Meeting</DialogTitle>
      <DialogContent sx={{ px: 0, pb: 0, pt: "10px !important" }}>
        <CreateEditTeamMeetingForm formState={formState} control={control} loadingEnabled={props.visible} />
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction="row" sx={{ pt: 1, width: "100%", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            color="error"
            disabled={isDeleting || isSaving}
            endIcon={isDeleting ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
            onClick={handleDeleteClicked}
          >
            Delete Meeting
          </Button>
          <Stack direction="row" spacing={0.5}>
            <Button variant="outlined" disabled={isSaving} onClick={handleCancelClicked}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isDeleting || isSaving}
              endIcon={isSaving ? <CircularProgress size={20} sx={{ color: "grey.400" }} /> : null}
              onClick={handleSaveClicked}
              sx={{ ml: "20px !important" }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export { EditTeamMeetingModal };
